import type { SanityPortableText } from '@libs/ui';

import type {
  SanityPublishedTripDetailsResponse,
  SanityPremadeTrip,
  PriceGuaranteeModalData,
} from '../../models';

import {
  mapDomainLocaleToSanityLang,
  mapFullLocaleToSanityCountry,
  mapResponseToPriceGuaranteeModal,
  mapResponseToPublishedTrip,
} from './sanityService.helper';

export class SanityService {
  // NOTE: We are using Sanity REST API to avoid including whole @sanity/client package in the bundle.
  // This URL was generated via Sanity Studio.
  private readonly BASE_URL =
    'https://inntox6v.apicdn.sanity.io/v2022-03-07/data/query/production?query=';
  private readonly FALLBACK_AI_TRIP_IMAGE_URL = `${this.BASE_URL}%0A*%5B%0A++++_type+%3D%3D+%22media%22+%26%26%0A++++_id+%3D%3D+%229c8131d6-f404-4962-8427-55dfb48f0700%22%0A%0A%5D%5B0%5D.image.asset-%3Eurl%0A`;

  public async fetchPublishedTripDetails(
    publishedTripId: string,
    lang: string,
    fullLocale: string
  ): Promise<SanityPremadeTrip | undefined> {
    const sanityLang = mapDomainLocaleToSanityLang(lang);
    const country = mapFullLocaleToSanityCountry(fullLocale);
    const apiUrl = this.getPublishedTripDetailsURL(
      publishedTripId,
      sanityLang,
      country
    );

    try {
      const response = await fetch(apiUrl);
      const jsonResponse = await response.json();
      const data: SanityPublishedTripDetailsResponse = jsonResponse.result;
      return mapResponseToPublishedTrip(data);
    } catch {
      throw new Error(
        'Could not fetch or parse published trip content from Sanity!'
      );
    }
  }

  public async fetchPriceGuaranteeModalMacrocopy(
    lang: string
  ): Promise<PriceGuaranteeModalData | undefined> {
    const sanityLang = mapDomainLocaleToSanityLang(lang);
    const apiUrl = this.getPriceGuaranteeModalMacrocopyURL(sanityLang);

    try {
      const response = await fetch(apiUrl);
      const jsonResponse = await response.json();
      return mapResponseToPriceGuaranteeModal(jsonResponse.result.value);
    } catch {
      throw new Error(
        'Could not fetch or parse published trip content from Sanity!'
      );
    }
  }

  public async fetchFlexibleFlightTicketReadMoreModalMacrocopy(
    lang: string
  ): Promise<SanityPortableText[] | undefined> {
    const sanityLang = mapDomainLocaleToSanityLang(lang);
    const apiUrl =
      this.getFlexibleFlightTicketReadMoreModalMacrocopyURL(sanityLang);

    try {
      const response = await fetch(apiUrl);
      const jsonResponse = await response.json();
      return jsonResponse.result.value;
    } catch {
      throw new Error(
        'Could not fetch or parse published trip content from Sanity!'
      );
    }
  }

  public async getFallbackAITripImageURL(): Promise<string | undefined> {
    try {
      const response = await fetch(this.FALLBACK_AI_TRIP_IMAGE_URL);
      const jsonResponse = await response.json();
      const data: string = jsonResponse.result;
      return data;
    } catch {
      throw new Error('Could not fetch fallback AI trip image!');
    }
  }

  // NOTE: We are using Sanity REST API to avoid including whole @sanity/client package in the bundle.
  // This URL was generated via Sanity Studio.
  private getPublishedTripDetailsURL(
    publishedTripId: string,
    lang: string,
    country: string
  ): string {
    const query = `
    *[
      _type == "trip" &&
      _id == "${publishedTripId}" &&
      "${country}" in coalesce(countries, ["${country}"])
    ] {
        "imgUrl": images[0]->image.asset->url,
        ...content${lang} {
          description,
          salesPitch,
          headline,
        }
      }[0]
    `;

    return `${this.BASE_URL}${this.encodeAndMinifyQuery(query)}`;
  }

  private getPriceGuaranteeModalMacrocopyURL(lang: string): string {
    const query = `
    *[_type == "macrocopy" && key == "explore.priceGuarantee.description"][0]
    {
      "value": pt::text(coalesce(value${lang}, valueEn))
    }
    `;

    return `${this.BASE_URL}${this.encodeAndMinifyQuery(query)}`;
  }

  private getFlexibleFlightTicketReadMoreModalMacrocopyURL(
    lang: string
  ): string {
    const query = `
    *[_type == "macrocopy" && key == "explore.flexibleTicketForFlights.readMore"][0]
    {
      "value": coalesce(value${lang}, valueEn)
    }
    `;

    return `${this.BASE_URL}${this.encodeAndMinifyQuery(query)}`;
  }

  private encodeAndMinifyQuery(query: string): string {
    return encodeURIComponent(
      query
        .replace(/\n/g, ' ')
        .replace(/\s{2,}/g, '')
        .trim()
    );
  }
}
